import { useMemo } from "react";
import { Helmet } from "react-helmet";
import { useAppSelector } from "@/hooks/types";
import { getTenant } from "@/store/tenant/selectors";
import { getEnv } from "@/utils/env";

interface PageHelmetProps {
    title: string;
    prefix?: string;
    description?: string;
    url?: string;
    robotsOverride?: string;
}

export const PageHelmet = ({
    title,
    description,
    url,
    prefix,
    robotsOverride,
}: PageHelmetProps) => {
    const env = getEnv();
    const tenant = useAppSelector(getTenant);
    const is1TemplateTenant = import.meta.env.VITE_OG_SITE_NAME === "1Template";
    const isNonProdEnv = env !== "production";
    const tenantName = import.meta.env.VITE_OG_SITE_NAME; // Use this as we will always have a tenant name

    const prefixContent = prefix ? `${prefix}${prefix && " | "}` : "";
    const titleContent = `${prefixContent}${tenantName} | ${title}`;
    const ogTitle = `${tenantName} | ${title}`;

    const favIconPath = useMemo(() => {
        if (!tenant || is1TemplateTenant) {
            return "/favicon.ico";
        } else {
            return tenant.images.favIcon;
        }
    }, [tenant, is1TemplateTenant]);

    // Simple URL handling
    const absoluteUrl = useMemo(() => {
        if (!url) return "";
        if (url.startsWith("http")) return url;
        return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
    }, [url]);

    // Determine robots content
    const robotsContent =
        robotsOverride ||
        (isNonProdEnv ? "noindex, nofollow" : "index, follow");

    return (
        <Helmet>
            <title>{titleContent}</title>
            {description && (
                <meta
                    name="description"
                    content={description}
                />
            )}
            {url && (
                <link
                    rel="canonical"
                    href={absoluteUrl}
                />
            )}
            {url && (
                <meta
                    property="og:url"
                    content={absoluteUrl}
                />
            )}
            <meta
                name="robots"
                content={robotsContent}
            />
            {title && (
                <meta
                    property="og:title"
                    content={ogTitle}
                />
            )}
            {description && (
                <meta
                    property="og:description"
                    content={description}
                />
            )}
            <meta
                property="og:type"
                content="website"
            />
            <meta
                property="og:site_name"
                content={tenantName}
            />
            <link
                rel="icon"
                href={favIconPath}
                media={
                    env === "production"
                        ? "(prefers-color-scheme: light)"
                        : undefined
                }
            />
            {env === "production" && is1TemplateTenant && (
                <link
                    rel="icon"
                    href="/favicon-dark.ico"
                    media="(prefers-color-scheme: dark)"
                />
            )}
        </Helmet>
    );
};
